import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../api/client.js";

export interface UseCommitArgs {
  readonly slug: string;
  readonly repo: string;
  readonly oid: string;
}

export interface CommitDetail {
  readonly oid: string;
  readonly message: string;
  readonly author: string;
  readonly timestamp: string;
  readonly parentOids: readonly string[];
}

// The server's wire shape (server/src/git/commit.ts CommitDetail).
interface WireCommit {
  readonly oid: string;
  readonly parents: readonly string[];
  readonly message: string;
  readonly author: { readonly name: string; readonly email: string; readonly timestamp: number };
}

export function useCommit(args: UseCommitArgs) {
  const { slug, repo, oid } = args;
  return useQuery({
    queryKey: ["commit", slug, repo, oid],
    queryFn: async ({ signal }): Promise<CommitDetail> => {
      const url =
        `/api/ws/${encodeURIComponent(slug)}/r/${encodeURIComponent(repo)}` +
        `/commit/${encodeURIComponent(oid)}`;
      // Normalize the server shape: parents→parentOids, author-object→name,
      // second-precision timestamp→ISO string (bugloop L3 — the client used to
      // read fields the server never sent and crashed the page).
      const raw = await apiFetch<WireCommit>(url, { signal });
      return {
        oid: raw.oid,
        message: raw.message,
        author: raw.author.name,
        timestamp: new Date(raw.author.timestamp * 1000).toISOString(),
        parentOids: raw.parents,
      };
    },
    enabled: Boolean(slug && repo && oid),
    staleTime: 5 * 60 * 1000,
  });
}
